/**
 * Centralized browser-storage library.
 *
 * The primary API is descriptor-driven: callers pass a `{ key, scope }` descriptor from
 * StorageKeys to the top-level resolver (`Storage.get`, `Storage.set`, `Storage.getRaw`,
 * `Storage.setRaw`, `Storage.getItem`, `Storage.setItem`, `Storage.remove`,
 * `Storage.removeMany`, `Storage.has`) and never name a backend — the scope is read off the
 * descriptor, so the tab-vs-shared decision lives only in keys.js. A plain string is also
 * accepted and resolves to the tab scope, for genuinely dynamic keys (a DataTables
 * instance, a searchform handle) that are not registry-backed.
 *
 * The two scope facades remain for those dynamic/plumbing keys and for scope-level ops:
 *   - Storage.tab    -> sessionStorage: per-browser-tab working state (filters,
 *     selected rows, paging, search state). Survives page refresh and same-tab
 *     navigation, but is isolated between tabs so two tabs on the same page no
 *     longer overwrite each other.
 *   - Storage.shared -> localStorage: state that is intentionally shared across all
 *     tabs of the origin (user preferences, long-term flags, cross-tab coordination).
 *     `Storage.persistent` is an alias of `Storage.shared`.
 *
 * A `seeded` descriptor (see keys.js) is stored in the tab scope like any other; the scope is
 * only a marker for the new-tab handoff (storage/handoff.js), which copies the origin tab's
 * seeded state into a candidate opened in a new tab. Nothing here mirrors seeded writes.
 *
 * Values passed to get()/set() are JSON-encoded; use getRaw()/setRaw() for plain
 * strings. Every operation is guarded: if storage access throws (private mode,
 * quota exceeded, storage disabled) it degrades to a no-op / the supplied default
 * instead of breaking the page.
 */

import { SCOPE_SHARED } from './keys';

const MISSING = null;

const makeFacade = (getBackend) => {
    const backend = () => {
        try {
            return getBackend();
        } catch {
            return null;
        }
    };

    const getRaw = (key, defaultValue = MISSING) => {
        const store = backend();
        if (!store) return defaultValue;
        try {
            const value = store.getItem(key);
            return value === null ? defaultValue : value;
        } catch {
            return defaultValue;
        }
    };

    const setRaw = (key, value) => {
        const store = backend();
        if (!store) return false;
        try {
            store.setItem(key, value);
            return true;
        } catch {
            return false;
        }
    };

    const get = (key, defaultValue = MISSING) => {
        const raw = getRaw(key, null);
        if (raw === null) return defaultValue;
        try {
            return JSON.parse(raw);
        } catch {
            return defaultValue;
        }
    };

    const set = (key, value) => {
        let encoded;
        try {
            encoded = JSON.stringify(value);
        } catch {
            return false;
        }
        return setRaw(key, encoded);
    };

    const remove = (key) => {
        const store = backend();
        if (!store) return false;
        try {
            store.removeItem(key);
            return true;
        } catch {
            return false;
        }
    };

    const removeMany = (keys) => {
        (keys || []).forEach((key) => remove(key));
    };

    const has = (key) => getRaw(key, null) !== null;

    // Clears the ENTIRE underlying backend, not just library-managed keys. Used by the
    // auth-transition purge on the `tab` scope (auth_purge.js); the `shared` scope is
    // wiped separately by the login-side pages' localStorage.clear().
    const clear = () => {
        const store = backend();
        if (!store) return false;
        try {
            store.clear();
            return true;
        } catch {
            return false;
        }
    };

    // Native `Storage`-compatible aliases (string in / string out, null when
    // missing). These let existing `localStorage.getItem(...)` call sites migrate by
    // swapping only the storage object, keeping the surrounding JSON.parse/stringify.
    // TODO: migration crutch — the remaining `JSON.parse(getItem(...))` call sites keep
    // serialization scattered and some field names (e.g. filtersAPI.localStorageKey) now lie
    // about the sessionStorage backend. Migrate stragglers to get()/set() and drop these aliases.
    const getItem = (key) => getRaw(key, null);
    const setItem = setRaw;
    const removeItem = remove;

    return {
        get,
        set,
        getRaw,
        setRaw,
        remove,
        removeMany,
        has,
        clear,
        getItem,
        setItem,
        removeItem,
    };
};

const Storage = {
    tab: makeFacade(() => window.sessionStorage),
    shared: makeFacade(() => window.localStorage),
};

Storage.persistent = Storage.shared;

// Descriptor-aware resolver (the primary API). Resolves a `{ key, scope }` descriptor to
// its backend, so call sites never mention `tab`/`shared`. A plain string resolves to the
// tab scope (dynamic, non-registry keys such as a DataTables instance or searchform handle).
// The `seeded` scope resolves to the tab backend too — it is only a registry marker for the
// new-tab handoff (see storage/handoff.js), not a distinct backend.
const resolve = (descriptor) => {
    if (typeof descriptor === 'string') {
        return { store: Storage.tab, key: descriptor };
    }
    const store = descriptor && descriptor.scope === SCOPE_SHARED ? Storage.shared : Storage.tab;
    return { store, key: descriptor ? descriptor.key : undefined };
};

Storage.get = (descriptor, defaultValue) => {
    const { store, key } = resolve(descriptor);
    return store.get(key, defaultValue);
};
Storage.set = (descriptor, value) => {
    const { store, key } = resolve(descriptor);
    return store.set(key, value);
};
Storage.getRaw = (descriptor, defaultValue) => {
    const { store, key } = resolve(descriptor);
    return store.getRaw(key, defaultValue);
};
Storage.setRaw = (descriptor, value) => {
    const { store, key } = resolve(descriptor);
    return store.setRaw(key, value);
};
Storage.getItem = (descriptor) => {
    const { store, key } = resolve(descriptor);
    return store.getItem(key);
};
Storage.setItem = (descriptor, value) => {
    const { store, key } = resolve(descriptor);
    return store.setItem(key, value);
};
Storage.remove = (descriptor) => {
    const { store, key } = resolve(descriptor);
    return store.remove(key);
};
Storage.removeItem = Storage.remove;
Storage.removeMany = (descriptors) => {
    (descriptors || []).forEach((descriptor) => Storage.remove(descriptor));
};
Storage.has = (descriptor) => {
    const { store, key } = resolve(descriptor);
    return store.has(key);
};

export default Storage;
export const tab = Storage.tab;
export const shared = Storage.shared;
export const persistent = Storage.persistent;
